import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/common/AppLayout';
import Button from '../components/common/Button';
import Spinner from '../components/common/Spinner';
import CoClosingAgentSelector from '../components/sales/CoClosingAgentSelector';
import api from '../services/api';
import { toast } from 'react-toastify';
import { formatPhoneInput, unformatPhone, isValidMalaysianPhone } from '../utils/phoneFormatter';
import { getLocationByPostcode, isValidPostcode } from '../utils/postcodeUtils';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { hasDirectorAccess } from '../utils/rolePermissions';

function CreateSalesFormPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id: salesFormId } = useParams(); // Get ID for edit mode
  const leadId = searchParams.get('leadId');
  const assignmentId = searchParams.get('assignmentId');

  const isEditMode = !!salesFormId; // Edit mode if ID exists
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [lead, setLead] = useState(null);
  const [assignment, setAssignment] = useState(null);
  const [existingSalesForm, setExistingSalesForm] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    // Client Information (pre-filled from lead)
    clientName: '',
    clientIC: '',
    clientPhone: '', // Store without +60 prefix (will be added on submit)
    clientEmail: '',

    // Property Details
    addressLine1: '',
    addressLine2: '',
    postcode: '',
    city: '',
    state: '',
    propertyType: 'condo',
    propertySize: '',

    // Budget & Pricing
    budgetMin: '',
    budgetMax: '',
    estimatedPackagePrice: '',

    // Deposit Information
    depositAmount: '',
    depositReceivedDate: new Date().toISOString().split('T')[0],
    depositProofs: [], // Changed to array for multiple files

    // Project Details
    projectScope: '',
    specialRequirements: '',
    preferredStartDate: ''
  });

  const [errors, setErrors] = useState({});
  const [isDragging, setIsDragging] = useState(false);

  // Mobile detection
  const isMobile = useMediaQuery('(max-width: 767px)');

  // Co-closing agents state
  const [coClosingAgents, setCoClosingAgents] = useState([]);
  const [primaryCloserId, setPrimaryCloserId] = useState(null);
  const [leadersWithAgents, setLeadersWithAgents] = useState([]);
  const [loadingLeaders, setLoadingLeaders] = useState(false);

  // Hierarchy selection state (for non-agent users: leader, manager, director)
  const [managersWithHierarchy, setManagersWithHierarchy] = useState([]);
  const [currentLeaderData, setCurrentLeaderData] = useState(null);
  const [selectedManagerId, setSelectedManagerId] = useState('');
  const [selectedLeaderId, setSelectedLeaderId] = useState('');
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [loadingHierarchy, setLoadingHierarchy] = useState(false);

  // Role-based flags
  const isAgent = user?.role === 'agent';
  const isLeader = user?.role === 'leader';
  const isManager = user?.role === 'manager';
  const isDirector = hasDirectorAccess(user?.role);
  const needsAgentSelection = isLeader || isManager || isDirector;

  // Property types - Malaysia specific
  const propertyTypes = [
    { value: 'apartment', label: 'Apartment/Flat' },
    { value: 'condo', label: 'Condominium' },
    { value: 'landed_single', label: 'Landed - Single Storey' },
    { value: 'landed_double', label: 'Landed - Double Storey' },
    { value: 'landed_triple', label: 'Landed - Triple Storey' },
    { value: 'terrace_single', label: 'Terrace House - Single Storey' },
    { value: 'terrace_double', label: 'Terrace House - Double Storey' },
    { value: 'terrace_triple', label: 'Terrace House - Triple Storey' },
    { value: 'semi_d_single', label: 'Semi-Detached - Single Storey' },
    { value: 'semi_d_double', label: 'Semi-Detached - Double Storey' },
    { value: 'semi_d_triple', label: 'Semi-Detached - Triple Storey' },
    { value: 'bungalow', label: 'Bungalow' },
    { value: 'townhouse', label: 'Townhouse' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    // Edit mode - fetch existing sales form
    if (isEditMode) {
      fetchExistingSalesForm();
    }
    // Create mode - If leadId and assignmentId provided, fetch lead data
    // Otherwise, it's a direct booking (no lead)
    else if (leadId && assignmentId) {
      fetchLeadData();
    } else {
      // Direct booking mode - no lead to fetch
      setLoading(false);
    }
  }, [salesFormId, leadId, assignmentId]);

  // Fetch leaders with their agents for co-closer selection
  useEffect(() => {
    async function fetchLeadersWithAgents() {
      try {
        setLoadingLeaders(true);
        const response = await api.get('/users/leaders-with-agents');
        setLeadersWithAgents(response.data.leaders || []);
      } catch (error) {
        console.error('Error fetching leaders:', error);
      } finally {
        setLoadingLeaders(false);
      }
    }
    fetchLeadersWithAgents();
  }, []);

  // Fetch hierarchy data for non-agent users (leader, manager, director)
  useEffect(() => {
    async function fetchHierarchy() {
      if (!needsAgentSelection || isEditMode) return;

      try {
        setLoadingHierarchy(true);
        const response = await api.get('/users/managers-with-hierarchy');

        if (response.data.userRole === 'leader') {
          // Leaders get their own team data
          setCurrentLeaderData(response.data.currentLeader);
        } else {
          // Managers and Directors get full hierarchy
          setManagersWithHierarchy(response.data.managers || []);
        }
      } catch (error) {
        console.error('Error fetching hierarchy:', error);
        toast.error('Failed to load team hierarchy');
      } finally {
        setLoadingHierarchy(false);
      }
    }
    fetchHierarchy();
  }, [needsAgentSelection, isEditMode]);

  // Compute available leaders based on selected manager (for cascading dropdown)
  const availableLeaders = useMemo(() => {
    if (isLeader) {
      // Leaders only see themselves - return empty since they don't need to select
      return [];
    }
    if (isManager) {
      // Managers see their own leaders
      const myTeam = managersWithHierarchy.find(m => m.id === user?.id);
      return myTeam?.salesTeams || [];
    }
    if (isDirector) {
      // Directors must first select a manager
      if (!selectedManagerId) return [];
      const manager = managersWithHierarchy.find(m => m.id === selectedManagerId);
      return manager?.salesTeams || [];
    }
    return [];
  }, [isLeader, isManager, isDirector, selectedManagerId, managersWithHierarchy, user?.id]);

  // Compute available agents based on selected leader (for cascading dropdown)
  const availableAgents = useMemo(() => {
    if (isLeader && currentLeaderData) {
      // Leaders see their own team agents
      return currentLeaderData.teamMembers || [];
    }
    if (!selectedLeaderId) return [];
    const leader = availableLeaders.find(l => l.id === selectedLeaderId);
    return leader?.teamMembers || [];
  }, [isLeader, currentLeaderData, selectedLeaderId, availableLeaders]);

  // Get selected agent details (for non-agent users)
  const selectedAgentDetails = useMemo(() => {
    if (!selectedAgentId) return null;
    return availableAgents.find(a => a.id === selectedAgentId) || null;
  }, [selectedAgentId, availableAgents]);

  // Determine the "main agent" for this form (for co-closing agent selector)
  // For agents: it's themselves
  // For non-agents: it's the selected agent
  const mainAgentId = isAgent ? user?.id : selectedAgentId;
  const mainAgentName = isAgent
    ? (user?.nickname || user?.fullName || 'Me')
    : (selectedAgentDetails?.nickname || selectedAgentDetails?.fullName || 'Selected Agent');

  async function fetchExistingSalesForm() {
    try {
      setLoading(true);

      const response = await api.get(`/sales-forms/${salesFormId}`);
      const salesForm = response.data.salesForm;

      // Check if form is rejected (only rejected forms can be edited)
      if (salesForm.status !== 'rejected') {
        toast.error('Only rejected sales forms can be edited');
        navigate('/dashboard');
        return;
      }

      setExistingSalesForm(salesForm);

      // Pre-fill form data - map backend field names to frontend
      setFormData({
        clientName: salesForm.clientName || '',
        clientIC: salesForm.clientIC || '',
        clientPhone: salesForm.clientPhone?.replace('+60', '') || '',
        clientEmail: salesForm.clientEmail || '',
        addressLine1: salesForm.addressLine1 || salesForm.propertyAddress || '',
        addressLine2: salesForm.addressLine2 || '',
        postcode: salesForm.postcode || '',
        city: salesForm.city || '',
        state: salesForm.state || '',
        propertyType: salesForm.propertyType || 'condo',
        propertySize: salesForm.propertySize || '',
        budgetMin: salesForm.budgetMin || '',
        budgetMax: salesForm.budgetMax || '',
        estimatedPackagePrice: salesForm.estimatedPackagePrice || '',
        depositAmount: salesForm.depositAmount || '',
        depositReceivedDate: salesForm.depositReceivedDate
          ? salesForm.depositReceivedDate.split('T')[0]
          : new Date().toISOString().split('T')[0],
        depositProofs: [],
        projectScope: salesForm.projectScope || '',
        specialRequirements: salesForm.specialRequirements || '',
        preferredStartDate: salesForm.preferredStartDate
          ? salesForm.preferredStartDate.split('T')[0]
          : ''
      });

      // Load co-closing agents if present
      if (salesForm.coClosingAgents && salesForm.coClosingAgents.length > 0) {
        setCoClosingAgents(salesForm.coClosingAgents);
      }
      if (salesForm.primaryCloserId) {
        setPrimaryCloserId(salesForm.primaryCloserId);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching sales form:', error);
      toast.error('Failed to load sales form');
      navigate('/dashboard');
    }
  }

  async function fetchLeadData() {
    try {
      setLoading(true);

      // Fetch lead details
      const leadResponse = await api.get(`/leads/${leadId}`);
      const leadData = leadResponse.data.lead;
      setLead(leadData);

      // Find the assignment
      const foundAssignment = leadData.assignments?.find(
        a => a.id === assignmentId
      );

      if (!foundAssignment) {
        toast.error('Assignment not found');
        navigate('/my-leads');
        return;
      }

      setAssignment(foundAssignment);

      // Pre-fill form with lead data
      // Extract phone number without +60 prefix for input field
      let phoneDigits = leadData.phoneNumber || '';
      phoneDigits = phoneDigits.replace(/\D/g, ''); // Remove non-digits
      if (phoneDigits.startsWith('60')) {
        phoneDigits = phoneDigits.substring(2);
      }
      if (phoneDigits.startsWith('0')) {
        phoneDigits = phoneDigits.substring(1);
      }

      setFormData(prev => ({
        ...prev,
        clientName: leadData.name || '',
        clientPhone: formatPhoneInput(phoneDigits),
        clientEmail: leadData.email || '',
        propertyType: leadData.propertyType || 'condo',
        budgetMin: leadData.budgetRange?.split('-')[0]?.trim() || '',
        budgetMax: leadData.budgetRange?.split('-')[1]?.trim() || ''
      }));
    } catch (error) {
      console.error('Error fetching lead:', error);
      toast.error('Failed to load lead data');
      navigate('/my-leads');
    } finally {
      setLoading(false);
    }
  }

  // Format IC number with auto-hyphen: YYMMDD-PB-XXXX
  function formatICNumber(value) {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');

    // Format with hyphens
    if (digits.length <= 6) {
      return digits;
    } else if (digits.length <= 8) {
      return `${digits.slice(0, 6)}-${digits.slice(6)}`;
    } else {
      return `${digits.slice(0, 6)}-${digits.slice(6, 8)}-${digits.slice(8, 12)}`;
    }
  }

  // Format number with commas for thousands
  function formatNumberWithCommas(value) {
    if (!value) return '';
    // Remove existing commas
    const numValue = value.toString().replace(/,/g, '');
    // Add commas
    return numValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // Remove commas from formatted number to get raw value
  function removeCommas(value) {
    return value.toString().replace(/,/g, '');
  }

  function handleInputChange(e) {
    const { name, value } = e.target;

    // Auto-format IC number
    if (name === 'clientIC') {
      const formatted = formatICNumber(value);
      setFormData(prev => ({ ...prev, [name]: formatted }));
    }
    // Auto-format phone number
    else if (name === 'clientPhone') {
      const formatted = formatPhoneInput(value);
      setFormData(prev => ({ ...prev, [name]: formatted }));
    }
    // Auto-format budget and price fields with commas
    else if (['budgetMin', 'budgetMax', 'estimatedPackagePrice', 'depositAmount'].includes(name)) {
      const rawValue = removeCommas(value);
      setFormData(prev => ({ ...prev, [name]: rawValue }));
    }
    // Auto-fill city and state based on postcode
    else if (name === 'postcode') {
      setFormData(prev => ({ ...prev, [name]: value }));

      // Auto-fill city and state when postcode is complete (5 digits)
      if (value.length === 5 && isValidPostcode(value)) {
        const location = getLocationByPostcode(value);
        if (location) {
          setFormData(prev => ({
            ...prev,
            city: location.city || prev.city,
            state: location.state || prev.state
          }));
          toast.success(`Auto-filled: ${location.city}, ${location.state}`);
        } else {
          toast.warning('Postcode not found. Please enter city and state manually.');
        }
      }
    }
    else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  }

  function validateFile(file) {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPEG, PNG, and PDF files are allowed');
      return false;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return false;
    }

    return true;
  }

  function handleFileChange(e) {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => validateFile(file));

    if (validFiles.length > 0) {
      setFormData(prev => ({
        ...prev,
        depositProofs: [...prev.depositProofs, ...validFiles]
      }));
    }

    // Reset input to allow selecting the same file again if needed
    e.target.value = '';
  }

  function removeFile(index) {
    setFormData(prev => ({
      ...prev,
      depositProofs: prev.depositProofs.filter((_, i) => i !== index)
    }));
  }

  // Drag and drop handlers
  function handleDragEnter(e) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }

  function handleDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }

  function handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const validFiles = files.filter(file => validateFile(file));

    if (validFiles.length > 0) {
      setFormData(prev => ({
        ...prev,
        depositProofs: [...prev.depositProofs, ...validFiles]
      }));
    }
  }

  function validateForm() {
    const newErrors = {};

    // Validate agent selection for non-agent users
    if (needsAgentSelection && !isEditMode && !selectedAgentId) {
      newErrors.selectedAgentId = 'Please select an agent for this booking';
    }

    // Required fields (must match backend validation in salesFormController.js)
    if (!formData.clientName.trim()) newErrors.clientName = 'Client name is required';
    if (!formData.addressLine1.trim()) newErrors.addressLine1 = 'Address Line 1 is required';
    if (!formData.postcode.trim()) newErrors.postcode = 'Postcode is required';
    if (!formData.estimatedPackagePrice) newErrors.estimatedPackagePrice = 'Estimated package price is required';
    if (!formData.depositAmount) newErrors.depositAmount = 'Deposit amount is required';
    // Optional fields: clientPhone, city, state, propertyType, propertySize, clientEmail, budgetMin, budgetMax, preferredStartDate, projectScope, specialRequirements

    // Validate numbers
    if (formData.budgetMin && isNaN(formData.budgetMin)) {
      newErrors.budgetMin = 'Must be a valid number';
    }
    if (formData.budgetMax && isNaN(formData.budgetMax)) {
      newErrors.budgetMax = 'Must be a valid number';
    }
    if (formData.estimatedPackagePrice && isNaN(formData.estimatedPackagePrice)) {
      newErrors.estimatedPackagePrice = 'Must be a valid number';
    }
    if (formData.depositAmount && isNaN(formData.depositAmount)) {
      newErrors.depositAmount = 'Must be a valid number';
    }
    if (formData.propertySize && isNaN(formData.propertySize)) {
      newErrors.propertySize = 'Must be a valid number';
    }

    // Validate IC format (basic Malaysian IC format: YYMMDD-PB-###G)
    const icPattern = /^\d{6}-\d{2}-\d{4}$/;
    if (formData.clientIC && !icPattern.test(formData.clientIC)) {
      newErrors.clientIC = 'IC format should be: YYMMDD-PB-###G (e.g., 920815-10-5678)';
    }

    // Validate phone format
    if (formData.clientPhone && !isValidMalaysianPhone(formData.clientPhone)) {
      newErrors.clientPhone = 'Invalid Malaysian phone number format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    try {
      setSubmitting(true);

      if (isEditMode) {
        // Edit mode - use PATCH with JSON data (no file upload for edits)
        const updateData = {
          clientName: formData.clientName,
          clientIC: formData.clientIC,
          clientPhone: unformatPhone(formData.clientPhone),
          clientEmail: formData.clientEmail || null,
          addressLine1: formData.addressLine1,
          addressLine2: formData.addressLine2 || null,
          postcode: formData.postcode,
          city: formData.city,
          state: formData.state,
          propertyType: formData.propertyType,
          propertySize: formData.propertySize || null,
          budgetMin: formData.budgetMin || null,
          budgetMax: formData.budgetMax || null,
          estimatedPackagePrice: formData.estimatedPackagePrice,
          depositAmount: formData.depositAmount,
          depositReceivedDate: formData.depositReceivedDate,
          projectScope: formData.projectScope || null,
          specialRequirements: formData.specialRequirements || null,
          preferredStartDate: formData.preferredStartDate || null,
          // Co-closing agents
          coClosingAgents: coClosingAgents.map(a => ({
            agentId: a.agentId,
            salesLeaderId: a.salesLeaderId
          })),
          primaryCloserId: primaryCloserId || null
        };

        await api.patch(`/sales-forms/${salesFormId}`, updateData);

        toast.success('Sales form updated and resubmitted successfully! Awaiting manager verification.');
        navigate(-1);
      } else {
        // Create mode - use FormData for file upload
        const submitData = new FormData();

        // Add leadId and assignmentId only if they exist (not direct booking)
        if (leadId) submitData.append('leadId', leadId);
        if (assignmentId) submitData.append('assignmentId', assignmentId);

        // Add selectedAgentId for non-agent users (leader, manager, director)
        if (needsAgentSelection && selectedAgentId) {
          submitData.append('selectedAgentId', selectedAgentId);
        }

        submitData.append('clientName', formData.clientName);
        submitData.append('clientIC', formData.clientIC);
        submitData.append('clientPhone', unformatPhone(formData.clientPhone));
        if (formData.clientEmail) submitData.append('clientEmail', formData.clientEmail);
        submitData.append('addressLine1', formData.addressLine1);
        if (formData.addressLine2) submitData.append('addressLine2', formData.addressLine2);
        submitData.append('postcode', formData.postcode);
        submitData.append('city', formData.city);
        submitData.append('state', formData.state);
        submitData.append('propertyType', formData.propertyType);
        if (formData.propertySize) submitData.append('propertySize', formData.propertySize);
        if (formData.budgetMin) submitData.append('budgetMin', formData.budgetMin);
        if (formData.budgetMax) submitData.append('budgetMax', formData.budgetMax);
        submitData.append('estimatedPackagePrice', formData.estimatedPackagePrice);
        submitData.append('depositAmount', formData.depositAmount);
        submitData.append('depositReceivedDate', formData.depositReceivedDate);
        if (formData.projectScope) submitData.append('projectScope', formData.projectScope);
        if (formData.specialRequirements) submitData.append('specialRequirements', formData.specialRequirements);
        if (formData.preferredStartDate) submitData.append('preferredStartDate', formData.preferredStartDate);

        // Add files if they exist
        if (formData.depositProofs && formData.depositProofs.length > 0) {
          formData.depositProofs.forEach(file => {
            submitData.append('depositProofs', file);
          });
        }

        // Add co-closing agents
        if (coClosingAgents.length > 0) {
          submitData.append('coClosingAgents', JSON.stringify(
            coClosingAgents.map(a => ({
              agentId: a.agentId,
              salesLeaderId: a.salesLeaderId
            }))
          ));
        }

        // Primary closer: use selected primary, or default to the main agent
        // mainAgentId is user.id for agents, or selectedAgentId for non-agents
        const finalPrimaryCloserId = primaryCloserId || mainAgentId;
        if (finalPrimaryCloserId) {
          submitData.append('primaryCloserId', finalPrimaryCloserId);
        }

        await api.post('/sales-forms', submitData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        toast.success('Sales form submitted successfully! Awaiting manager verification.');
        navigate(-1);
      }
    } catch (error) {
      console.error('Error submitting sales form:', error);
      // Handle both raw axios errors and interceptor-transformed errors
      const errorMessage = error.response?.data?.error || error.data?.error || error.message || 'Failed to submit sales form';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <Spinner size="lg" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      {/* Page Header */}
      <div className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">
              {isEditMode ? 'Edit & Resubmit Sales Form' : (leadId ? 'Create Sales Form' : 'Direct Booking - Create Sales Form')}
            </h1>
            <p className="page-subtitle">
              {isEditMode
                ? 'Update the rejected form and resubmit for manager verification'
                : (leadId
                  ? `Book a new project for ${lead?.name}`
                  : 'Create booking for walk-in client or referral (no existing lead)')
              }
            </p>
          </div>
          <Button
            variant="secondary"
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
        </div>
      </div>

      {/* Lead Info Card - Only show if lead exists */}
      {lead && (
        <div className="card mb-6">
          <h2 className="text-lg font-display font-bold mb-4">Lead Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-gray-600">Lead Name</p>
              <p className="font-medium">{lead?.name}</p>
            </div>
            <div>
              <p className="text-gray-600">Phone</p>
              <p className="font-medium">{lead?.phoneNumber}</p>
            </div>
            <div>
              <p className="text-gray-600">Status</p>
              <p className="font-medium capitalize">{assignment?.status?.replace('_', ' ')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Reason Alert - Only show in edit mode */}
      {isEditMode && existingSalesForm?.rejectionReason && (
        <div className="card mb-6 bg-red-50 border-red-200">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <h3 className="font-semibold text-red-900 mb-1">Form Rejected - Manager's Feedback</h3>
              <p className="text-sm text-red-800 font-medium mb-2">Please address the following issues before resubmitting:</p>
              <div className="bg-white border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-900">{existingSalesForm.rejectionReason}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Direct Booking Notice - Only show if no lead */}
      {!leadId && !isEditMode && (
        <div className="card mb-6 bg-blue-50 border-blue-200">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="font-semibold text-blue-900 mb-1">Direct Booking Mode</h3>
              <p className="text-sm text-blue-800">
                This form is for clients who booked directly (walk-ins, referrals, or clients not in the lead system).
                A lead record will be automatically created when you submit this form.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Sales Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Agent Selection - For Leaders, Managers, Directors */}
        {needsAgentSelection && !isEditMode && (
          <div className="card">
            <h2 className="text-xl font-display font-bold mb-4">Select Sales Agent</h2>
            <p className="text-sm text-gray-600 mb-6">
              As a {user?.role}, you must select an agent for this booking. The sale will be attributed to the selected agent.
            </p>

            {loadingHierarchy ? (
              <div className="flex items-center justify-center py-8">
                <Spinner size="sm" />
                <span className="ml-2 text-gray-500">Loading team hierarchy...</span>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Manager Selection - Only for Directors */}
                {isDirector && (
                  <div>
                    <label className="label">
                      Manager <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={selectedManagerId}
                      onChange={(e) => {
                        setSelectedManagerId(e.target.value);
                        setSelectedLeaderId('');
                        setSelectedAgentId('');
                      }}
                      className="input"
                    >
                      <option value="">-- Select Manager --</option>
                      {managersWithHierarchy.map(manager => (
                        <option key={manager.id} value={manager.id}>
                          {manager.nickname || manager.fullName}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Leader Selection - For Directors and Managers */}
                {(isDirector || isManager) && (
                  <div>
                    <label className="label">
                      Leader <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={selectedLeaderId}
                      onChange={(e) => {
                        setSelectedLeaderId(e.target.value);
                        setSelectedAgentId('');
                      }}
                      className="input"
                      disabled={isDirector && !selectedManagerId}
                    >
                      <option value="">-- Select Leader --</option>
                      {availableLeaders.map(leader => (
                        <option key={leader.id} value={leader.id}>
                          {leader.nickname || leader.fullName}
                        </option>
                      ))}
                    </select>
                    {isDirector && !selectedManagerId && (
                      <p className="text-gray-500 text-xs mt-1">Select a manager first</p>
                    )}
                  </div>
                )}

                {/* Agent Selection - For All Non-Agents */}
                <div>
                  <label className="label">
                    Agent <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={selectedAgentId}
                    onChange={(e) => setSelectedAgentId(e.target.value)}
                    className={`input ${errors.selectedAgentId ? 'border-red-500' : ''}`}
                    disabled={(isDirector || isManager) && !selectedLeaderId}
                  >
                    <option value="">-- Select Agent --</option>
                    {availableAgents.map(agent => (
                      <option key={agent.id} value={agent.id}>
                        {agent.nickname || agent.fullName}
                      </option>
                    ))}
                  </select>
                  {(isDirector || isManager) && !selectedLeaderId && (
                    <p className="text-gray-500 text-xs mt-1">Select a leader first</p>
                  )}
                  {errors.selectedAgentId && (
                    <p className="text-red-500 text-sm mt-1">{errors.selectedAgentId}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Client Information */}
        <div className="card">
          <h2 className="text-xl font-display font-bold mb-6">Client Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="label">
                Client Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="clientName"
                value={formData.clientName}
                onChange={handleInputChange}
                className={`input ${errors.clientName ? 'border-red-500' : ''}`}
                placeholder="John Doe"
                required
              />
              {errors.clientName && (
                <p className="text-red-500 text-sm mt-1">{errors.clientName}</p>
              )}
            </div>

            <div>
              <label className="label">
                Client IC Number
              </label>
              <input
                type="text"
                name="clientIC"
                value={formData.clientIC}
                onChange={handleInputChange}
                className={`input ${errors.clientIC ? 'border-red-500' : ''}`}
                placeholder="920815-10-5678"
              />
              {errors.clientIC && (
                <p className="text-red-500 text-sm mt-1">{errors.clientIC}</p>
              )}
              <p className="text-gray-500 text-xs mt-1">Format: YYMMDD-PB-###G</p>
            </div>

            <div>
              <label className="label">
                Client Phone
              </label>
              <div className="flex gap-2">
                <div className="flex items-center px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-700 font-medium">
                  +60
                </div>
                <input
                  type="tel"
                  name="clientPhone"
                  value={formData.clientPhone}
                  onChange={handleInputChange}
                  className={`input flex-1 ${errors.clientPhone ? 'border-red-500' : ''}`}
                  placeholder="12-345 6789"
                  maxLength="13"
                />
              </div>
              {errors.clientPhone && (
                <p className="text-red-500 text-sm mt-1">{errors.clientPhone}</p>
              )}
              <p className="text-gray-500 text-xs mt-1">Format: XX-XXXX XXXX</p>
            </div>

            <div>
              <label className="label">
                Client Email
              </label>
              <input
                type="email"
                name="clientEmail"
                value={formData.clientEmail}
                onChange={handleInputChange}
                className={`input ${errors.clientEmail ? 'border-red-500' : ''}`}
                placeholder="john@example.com"
              />
              {errors.clientEmail && (
                <p className="text-red-500 text-sm mt-1">{errors.clientEmail}</p>
              )}
            </div>
          </div>
        </div>

        {/* Property Details */}
        <div className="card">
          <h2 className="text-xl font-display font-bold mb-6">Property Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="label">
                Address Line 1 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="addressLine1"
                value={formData.addressLine1}
                onChange={handleInputChange}
                className={`input ${errors.addressLine1 ? 'border-red-500' : ''}`}
                placeholder="Street address, P.O. box, company name, c/o"
                required
              />
              {errors.addressLine1 && (
                <p className="text-red-500 text-sm mt-1">{errors.addressLine1}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="label">
                Address Line 2
              </label>
              <input
                type="text"
                name="addressLine2"
                value={formData.addressLine2}
                onChange={handleInputChange}
                className="input"
                placeholder="Apartment, suite, unit, building, floor, etc."
              />
            </div>

            <div>
              <label className="label">
                Postcode <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="postcode"
                value={formData.postcode}
                onChange={handleInputChange}
                className={`input ${errors.postcode ? 'border-red-500' : ''}`}
                placeholder="43000"
                maxLength="5"
                required
              />
              {errors.postcode && (
                <p className="text-red-500 text-sm mt-1">{errors.postcode}</p>
              )}
              <p className="text-gray-500 text-xs mt-1">
                💡 City and state will auto-fill
              </p>
            </div>

            <div>
              <label className="label">
                City
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                className={`input ${errors.city ? 'border-red-500' : ''}`}
                placeholder="Kajang"
              />
              {errors.city && (
                <p className="text-red-500 text-sm mt-1">{errors.city}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="label">
                State
              </label>
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                className={`input ${errors.state ? 'border-red-500' : ''}`}
                placeholder="Selangor"
              />
              {errors.state && (
                <p className="text-red-500 text-sm mt-1">{errors.state}</p>
              )}
            </div>

            <div>
              <label className="label">
                Property Type
              </label>
              <select
                name="propertyType"
                value={formData.propertyType}
                onChange={handleInputChange}
                className={`input ${errors.propertyType ? 'border-red-500' : ''}`}
              >
                {propertyTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              {errors.propertyType && (
                <p className="text-red-500 text-sm mt-1">{errors.propertyType}</p>
              )}
            </div>

            <div>
              <label className="label">
                Property Size (sq ft)
              </label>
              <input
                type="number"
                name="propertySize"
                value={formData.propertySize}
                onChange={handleInputChange}
                className={`input ${errors.propertySize ? 'border-red-500' : ''}`}
                placeholder="1200.50"
                step="0.01"
              />
              {errors.propertySize && (
                <p className="text-red-500 text-sm mt-1">{errors.propertySize}</p>
              )}
            </div>
          </div>
        </div>

        {/* Budget & Pricing */}
        <div className="card">
          <h2 className="text-xl font-display font-bold mb-6">Budget & Pricing</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="label">
                Budget Min (RM)
              </label>
              <input
                type="text"
                name="budgetMin"
                value={formatNumberWithCommas(formData.budgetMin)}
                onChange={handleInputChange}
                className={`input ${errors.budgetMin ? 'border-red-500' : ''}`}
                placeholder="40,000.00"
              />
              {errors.budgetMin && (
                <p className="text-red-500 text-sm mt-1">{errors.budgetMin}</p>
              )}
            </div>

            <div>
              <label className="label">
                Budget Max (RM)
              </label>
              <input
                type="text"
                name="budgetMax"
                value={formatNumberWithCommas(formData.budgetMax)}
                onChange={handleInputChange}
                className={`input ${errors.budgetMax ? 'border-red-500' : ''}`}
                placeholder="60,000.00"
              />
              {errors.budgetMax && (
                <p className="text-red-500 text-sm mt-1">{errors.budgetMax}</p>
              )}
            </div>

            <div>
              <label className="label">
                Estimated Package Price (RM) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="estimatedPackagePrice"
                value={formatNumberWithCommas(formData.estimatedPackagePrice)}
                onChange={handleInputChange}
                className={`input ${errors.estimatedPackagePrice ? 'border-red-500' : ''}`}
                placeholder="50,000.00"
                required
              />
              {errors.estimatedPackagePrice && (
                <p className="text-red-500 text-sm mt-1">{errors.estimatedPackagePrice}</p>
              )}
              <p className="text-gray-500 text-xs mt-1">Initial quote to client</p>
            </div>
          </div>
        </div>

        {/* Deposit Information */}
        <div className="card">
          <h2 className="text-xl font-display font-bold mb-6">Deposit Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="label">
                Deposit Amount (RM) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="depositAmount"
                value={formatNumberWithCommas(formData.depositAmount)}
                onChange={handleInputChange}
                className={`input ${errors.depositAmount ? 'border-red-500' : ''}`}
                placeholder="5,000.00"
                required
              />
              {errors.depositAmount && (
                <p className="text-red-500 text-sm mt-1">{errors.depositAmount}</p>
              )}
            </div>

            <div>
              <label className="label">
                Deposit Received Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="depositReceivedDate"
                value={formData.depositReceivedDate}
                onChange={handleInputChange}
                className="input"
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="label">
                Deposit Proof (Upload - Multiple files allowed)
              </label>

              {/* Existing files from database - Only show in edit mode */}
              {isEditMode && existingSalesForm?.depositProofFiles && existingSalesForm.depositProofFiles.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Previously Uploaded Files:</p>
                  <div className="space-y-2">
                    {existingSalesForm.depositProofFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <svg className="h-8 w-8 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <div>
                            <p className="text-sm font-medium text-blue-900">{file.originalName || file.filename}</p>
                            {file.size && (
                              <p className="text-xs text-blue-700">
                                {(file.size / 1024 / 1024).toFixed(2)} MB
                              </p>
                            )}
                          </div>
                        </div>
                        <a
                          href={file.url ? (file.url.startsWith('http') ? file.url : `${(process.env.REACT_APP_API_URL || 'http://localhost:5001/api').replace(/\/api$/, '')}${file.url}`) : '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-3 py-1.5 border border-blue-300 text-xs font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          View
                        </a>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Note: You cannot re-upload receipt files when editing. If you need to change the receipt, please contact your manager.
                  </p>
                </div>
              )}

              {/* Uploaded files list - Only show in create mode */}
              {!isEditMode && formData.depositProofs.length > 0 && (
                <div className="mb-4 space-y-2">
                  {formData.depositProofs.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <svg className="h-8 w-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                          <p className="text-sm font-medium text-green-900">{file.name}</p>
                          <p className="text-xs text-green-700">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-red-600 hover:text-red-800 p-2"
                        title="Remove file"
                      >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload zone - Only show in create mode */}
              {!isEditMode && (
                isMobile ? (
                  /* Mobile Upload - Large touch-friendly buttons */
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      {/* Camera capture button */}
                      <label className="flex flex-col items-center justify-center gap-2 p-4 bg-amber-50 border-2 border-amber-200 rounded-xl cursor-pointer active:bg-amber-100 transition-colors">
                        <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="text-sm font-medium text-amber-700">Take Photo</span>
                        <input
                          type="file"
                          className="hidden"
                          onChange={handleFileChange}
                          accept="image/*"
                          capture="environment"
                        />
                      </label>

                      {/* Gallery/file picker button */}
                      <label className="flex flex-col items-center justify-center gap-2 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl cursor-pointer active:bg-blue-100 transition-colors">
                        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-sm font-medium text-blue-700">Choose File</span>
                        <input
                          type="file"
                          className="hidden"
                          onChange={handleFileChange}
                          accept=".jpg,.jpeg,.png,.pdf"
                          multiple
                        />
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 text-center">
                      JPEG, PNG, PDF up to 10MB each
                    </p>
                    {formData.depositProofs.length > 0 && (
                      <p className="text-xs text-green-600 font-medium text-center">
                        {formData.depositProofs.length} file(s) ready to upload
                      </p>
                    )}
                  </div>
                ) : (
                  /* Desktop - Drag and drop */
                  <div
                    className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                      isDragging
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <div className="space-y-2">
                      <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                        <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <div className="flex text-sm text-gray-600 justify-center">
                        <label htmlFor="deposit-file-upload" className="relative cursor-pointer rounded-md font-medium text-primary-600 hover:text-primary-500">
                          <span>{formData.depositProofs.length > 0 ? 'Add more files' : 'Upload files'}</span>
                          <input
                            id="deposit-file-upload"
                            type="file"
                            className="sr-only"
                            onChange={handleFileChange}
                            accept=".jpg,.jpeg,.png,.pdf"
                            multiple
                          />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-500">
                        JPEG, PNG, PDF up to 10MB each (Max 5 files)
                      </p>
                      {formData.depositProofs.length > 0 && (
                        <p className="text-xs text-green-600 font-medium">
                          {formData.depositProofs.length} file(s) uploaded
                        </p>
                      )}
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </div>

        {/* Project Details */}
        <div className="card">
          <h2 className="text-xl font-display font-bold mb-6">Project Details</h2>
          <div className="space-y-6">
            <div>
              <label className="label">
                Project Scope <span className="text-gray-400 text-xs">(Optional)</span>
              </label>
              <textarea
                name="projectScope"
                value={formData.projectScope}
                onChange={handleInputChange}
                className="input"
                rows="4"
                placeholder="Full house renovation including kitchen, living room, 3 bedrooms..."
              />
              <p className="text-gray-500 text-xs mt-1">
                Describe the scope of work for this project
              </p>
            </div>

            <div>
              <label className="label">
                Special Requirements <span className="text-gray-400 text-xs">(Optional)</span>
              </label>
              <textarea
                name="specialRequirements"
                value={formData.specialRequirements}
                onChange={handleInputChange}
                className="input"
                rows="3"
                placeholder="Client prefers minimalist design, eco-friendly materials..."
              />
              <p className="text-gray-500 text-xs mt-1">
                Any special requests or preferences from the client
              </p>
            </div>

            <div>
              <label className="label">
                Preferred Start Date
              </label>
              <input
                type="date"
                name="preferredStartDate"
                value={formData.preferredStartDate}
                onChange={handleInputChange}
                className={`input ${errors.preferredStartDate ? 'border-red-500' : ''}`}
              />
              {errors.preferredStartDate && (
                <p className="text-red-500 text-sm mt-1">{errors.preferredStartDate}</p>
              )}
            </div>
          </div>
        </div>

        {/* Co-Closing Agents */}
        <div className="card">
          <h2 className="text-xl font-display font-bold mb-2">
            Co-Closing Agents
            <span className="text-sm font-normal text-gray-500 ml-2">(Optional)</span>
          </h2>
          <p className="text-sm text-gray-600 mb-6">
            Add up to 3 additional agents who collaborated on closing this sale.
            You can designate who the primary closer is.
          </p>

          {loadingLeaders ? (
            <div className="flex items-center justify-center py-8">
              <Spinner size="sm" />
              <span className="ml-2 text-gray-500">Loading team data...</span>
            </div>
          ) : (
            <CoClosingAgentSelector
              coClosingAgents={coClosingAgents}
              primaryCloserId={primaryCloserId}
              submittingAgentId={mainAgentId}
              submittingAgentName={mainAgentName}
              leadersWithAgents={leadersWithAgents}
              onChange={setCoClosingAgents}
              onPrimaryChange={setPrimaryCloserId}
              maxAgents={3}
              disabled={submitting || (needsAgentSelection && !selectedAgentId)}
            />
          )}
        </div>

        {/* Submit Buttons */}
        <div className="card">
          <div className="flex items-center justify-end gap-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate(-1)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  {isEditMode ? 'Resubmitting...' : 'Submitting...'}
                </>
              ) : (
                isEditMode ? 'Update & Resubmit' : 'Submit Sales Form'
              )}
            </Button>
          </div>
          <p className="text-gray-500 text-sm mt-4 text-center">
            {isEditMode
              ? 'After resubmission, the form status will be reset to pending and sent to the manager for re-verification.'
              : 'After submission, this form will be sent to the manager for verification.'
            }
          </p>
        </div>
      </form>
    </AppLayout>
  );
}

export default CreateSalesFormPage;
